import { User, WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderNoUserManagerProps } from 'react-oidc-context';

// Params Keycloak appends to the redirect_uri when it sends the user back. They are
// reserved — app query params (nuqs keys included) must not use these names, they are
// stripped from every URL below.
const AUTH_PARAMS = ['code', 'state', 'session_state', 'iss', 'error', 'error_description', 'error_uri'];

// They must never survive into the next authorize request: redirect_uri is frozen at
// module load, so leftovers get echoed back on top of the fresh params — and
// oidc-client-ts reads the *first* `state`, which is then the already consumed one,
// failing with "No matching state found in storage" (LC-980).
export const stripAuthParams = (url: URL) => {
  for (const param of AUTH_PARAMS) {
    url.searchParams.delete(param);
  }
  return url;
};

export const currentUrlWithoutAuthParams = () => stripAuthParams(new URL(window.location.href)).toString();

export const currentPathWithoutAuthParams = () => {
  const url = stripAuthParams(new URL(window.location.href));
  return url.pathname + url.search;
};

// redirect_uri keeps deep link and query params (nuqs filters) across the login, minus the auth params
// monitorSession needs third-party cookies; single session logout runs over websockets instead
export const createOidcConfig = (keycloakUrl: string, clientId: string): AuthProviderNoUserManagerProps => ({
  authority: `${keycloakUrl}/realms/medcom`,
  client_id: clientId,
  redirect_uri: currentUrlWithoutAuthParams(),
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  onSigninCallback: () => window.history.replaceState({}, document.title, currentPathWithoutAuthParams()),
  monitorSession: false,
});

// the key oidc-client-ts stores the signed-in user under, with the sessionStorage userStore above
const storedUserKey = ({ authority, client_id }: AuthProviderNoUserManagerProps) => `oidc.user:${authority}:${client_id}`;

export const loadStoredUser = (config: AuthProviderNoUserManagerProps) => {
  const userState = window.sessionStorage.getItem(storedUserKey(config));
  return userState ? User.fromStorageString(userState) : null;
};

export const forgetStoredUser = (config: AuthProviderNoUserManagerProps) => window.sessionStorage.removeItem(storedUserKey(config));

const SESSION_EXPIRED_PATH = '/sitzung-abgelaufen';

export const isSessionExpiredPage = (location: Location = window.location) => location.pathname === SESSION_EXPIRED_PATH;

// A renew failing on the page itself must not make it its own return target
export const sessionExpiredPath = () =>
  `${SESSION_EXPIRED_PATH}?${new URLSearchParams({ from: isSessionExpiredPage() ? '/' : currentPathWithoutAuthParams() })}`;
